import { useState } from 'react'
import styled from 'styled-components'
import { P, Button, Wrapper } from '@farewill/ui'
import { AttachmentIcon } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import type { DocumentWithAttributes } from 'lib/models/document'
import { DOCUMENT_CATEGORIES } from 'utils/enums'
import { showModal } from 'state/actions'
import { useDocumentsContext } from '../context'
import DeleteModal from '../delete-modal'
import { EditModal } from '../edit-modal'
import { getUploadedByWithTime } from '../helpers'

const StyledDocumentCard = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  border-color: ${COLOR.GREY.DARK};
  border-style: dashed;
  border-width: 1px;
  border-radius: ${GTR.XS};
  gap: ${GTR.XS};
`

const StyledDocumentCardRow = styled(Wrapper)<{
  $align: 'start' | 'center' | 'end'
}>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ $align }) => $align};
  gap: ${GTR.XS};
`

const StyledDocumentCardTitle = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.XXS};
`

const StyledDocumentCardTitleCategory = styled(P)`
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledDocumentLink = styled(Link)`
  display: inline-block;
  font-size: ${FONT.SIZE.M};
  line-height: 1;
`

const StyledActionButton = styled(Button.Underline)<{ $color: string }>`
  padding: 0;
  margin: 0;
  color: ${({ $color }) => $color};
  font-weight: ${FONT.WEIGHT.REGULAR};
  font-size: ${FONT.SIZE.S};
`

const StyledActivityTimestamp = styled(P)`
  color: ${COLOR.GREY.DARK};
  font-size: ${FONT.SIZE.XS};
  margin: 0;
`

export const DocumentCardReview = ({
  document,
}: {
  document: DocumentWithAttributes
}) => {
  const dispatch = useDispatch()
  const {
    saveDocument,
    deleteDocument,
    isDeletingDocument,
    updateFile,
    updateDocument,
  } = useDocumentsContext()
  const [isSaving, setIsSaving] = useState(false)

  const handleEditClick = () => {
    dispatch(
      showModal({
        component: EditModal,
        headingText: 'Edit document',
        maxWidth: 740,
        config: {
          document,
          updateFile,
          updateDocument,
        },
      })
    )
  }

  const handleDeleteClick = () => {
    dispatch(
      showModal({
        component: DeleteModal,
        headingText: 'Remove document',
        config: {
          documentId: document.id,
          deleteDocument,
          isDeletingDocument,
        },
      })
    )
  }

  const handleSaveToCase = async () => {
    try {
      setIsSaving(true)
      await saveDocument(document.id)
    } finally {
      setIsSaving(false)
    }
  }

  const category = DOCUMENT_CATEGORIES.find(
    (category) => category.value === document.attributes.category
  )

  return (
    <Wrapper as="li" key={document.id} margin={[0, 0, 'M', 0]}>
      <StyledDocumentCard
        key={document.id}
        padding="S"
        background={COLOR.WHITE}
        margin={['0', '0', GTR.XXS, '0']}
      >
        <StyledDocumentCardRow $align="center">
          <StyledDocumentCardTitle>
            <StyledDocumentCardTitleCategory margin="0" color={COLOR.GREY.DARK}>
              {category?.label}:
            </StyledDocumentCardTitleCategory>
            <AttachmentIcon color={COLOR.GREY.DARK} size="S" />
            <StyledDocumentLink
              to={`/probate/cases/${document.attributes.probateCaseId}/documents/${document.id}`}
            >
              {document.attributes.title}
            </StyledDocumentLink>
          </StyledDocumentCardTitle>
          <StyledActionButton
            $color={COLOR.GREY.DARK}
            onClick={handleEditClick}
          >
            Edit
          </StyledActionButton>
        </StyledDocumentCardRow>
        <StyledDocumentCardRow $align="end">
          <StyledActionButton
            $color={COLOR.STATE.ERROR}
            onClick={handleDeleteClick}
          >
            Delete
          </StyledActionButton>
          <Button.Bordered
            size="S"
            loading={isSaving}
            disabled={isSaving}
            onClick={handleSaveToCase}
          >
            Save to case
          </Button.Bordered>
        </StyledDocumentCardRow>
      </StyledDocumentCard>
      <StyledActivityTimestamp>
        {getUploadedByWithTime(document)}
      </StyledActivityTimestamp>
    </Wrapper>
  )
}
